import { buildEdgeAuthStartUrl } from '@cortex/core-types';
import { logError, logEvent } from '@cortex/faro';
import { config } from '../config.js';
import type { CurrentUserResponse } from '../types/api.js';
import type {
  WeeklyReport,
  WeeklyReportDetailResponse,
  WeeklyReportInput,
  WeeklyReportListResponse,
} from '../types/weekly-report.js';
import type { RecordsResponse } from '../types/kpi.js';
import type { BreakthroughResponse } from '../types/breakthrough.js';
import type {
  RawDataResponse,
  RawDataTableKey,
  RevenueRecordsResponse,
  ValidationLogsResponse,
} from '../types/revenue.js';
import type { ScreenMetaInput, ScreenMetaResponse, ScreensResponse } from '../types/code-map.js';
import type { TimelineResponse } from '../types/release.js';
import type { CatalogColumnsResponse, CatalogOverviewResponse } from '../types/data-catalog.js';
import type { WithdrawalAnalysisResponse } from '../types/withdrawal-analysis.js';
import type {
  CsAiAnalysisHistoryItem,
  CsDashboardResponse,
  CsChatsResponse,
  CsChatDetailResponse,
  CsImportStatusResponse,
  SavedCsAiAnalysisResponse,
} from '../types/cs-inquiry.js';
import type { FunnelResponse } from '../types/funnel-analysis.js';
import type {
  KpiSummaryResponse,
  EvaluationTrendResponse,
  RentalHistoryResponse,
  OrderDetailResponse,
} from '../types/stylist-performance.js';
export { dataIntegrityApi } from './data-integrity-api.js';
import type {
  MemberListResponse,
  MemberProfileResponse,
  PaymentHistoryResponse,
  OrderHistoryResponse,
  TicketHistoryResponse,
  CouponHistoryResponse,
  AddressListResponse,
} from '../types/member.js';
import type {
  ChannelListResponse as NotifChannelListResponse,
  ChannelMutationResponse as NotifChannelMutationResponse,
  RuleListResponse,
  RuleMutationResponse,
  SlackChannelInput,
  NotificationRuleInput,
  TestNotificationInput,
  TestNotificationResponse,
} from '../types/notification.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business aC Men's Hub frontend の API エラー型。HTTP ステータスと業務コードを保持する。
 * @graph-connects none
 */
export class ApiError extends Error {
  /** @graph-connects none */
  constructor(
    message: string,
    public status: number,
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ErrorResponse {
  error?: {
    message?: string;
    code?: string;
  };
}

/** @graph-connects none */
export function dateRangeUrl(path: string, from?: string, to?: string): string {
  const qs = new URLSearchParams();
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  const query = qs.toString();
  return query ? `${path}?${query}` : path;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business aC Men's Hub API に対して Cookie 認証付き JSON リクエストを行う共有 fetch ラッパ。401 は Edge Auth 開始 or /login へ誘導する。
 * @graph-connects /auth/me [calls] Current user endpoint
 * @graph-connects /auth/logout [calls] Logout endpoint
 */
export async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...Object.fromEntries(
      options.headers instanceof Headers
        ? options.headers.entries()
        : Object.entries(options.headers ?? {}),
    ),
  };

  const method = options.method ?? 'GET';

  let response: Response;
  try {
    response = await fetch(`${config.apiUrl}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });
  } catch (fetchError) {
    // ネットワーク到達不能・CORS 拒否など fetch 自体が reject するケース。context 付きで Faro に投げてから再 throw。
    logError(fetchError instanceof Error ? fetchError : new Error(String(fetchError)), {
      event: 'api_request_network_failed',
      endpoint,
      method,
    });
    throw fetchError;
  }

  if (response.status === 401) {
    logEvent('api_request_unauthorized', {
      endpoint,
      method,
      edgeAuthStart: response.headers.get('X-Edge-Auth-Start') ? 'true' : 'false',
    });
    if (response.headers.get('X-Edge-Auth-Start')) {
      window.location.href = buildEdgeAuthStartUrl(window.location.href);
    } else if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    throw new ApiError('Unauthorized', 401, 'UNAUTHORIZED');
  }

  if (!response.ok) {
    const error = (await response.json().catch((parseError: unknown) => {
      logError(parseError instanceof Error ? parseError : new Error(String(parseError)), {
        event: 'error_response_body_parse_failed',
        endpoint,
        method,
        status: String(response.status),
      });
      return {} as ErrorResponse;
    })) as ErrorResponse;
    logEvent('api_request_failed', {
      endpoint,
      method,
      status: String(response.status),
      code: error.error?.code ?? '',
    });
    throw new ApiError(
      error.error?.message ?? `HTTP ${String(response.status)}`,
      response.status,
      error.error?.code,
    );
  }

  if (response.status === 204) {
    return null as T;
  }

  return response.json() as Promise<T>;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 認証 API クライアント。getCurrentUser はブートストラップ用で未ログイン(401)時は例外ではなく null を返す。login の OAuth 開始は Edge Router Worker が配信する。
 * @graph-connects /auth/me [calls] Current user endpoint
 * @graph-connects /auth/logout [calls] Logout endpoint
 */
export const authApi = {
  getCurrentUser: async (): Promise<CurrentUserResponse | null> => {
    try {
      return await request<CurrentUserResponse>('/auth/me');
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        return null;
      }
      throw error;
    }
  },
  logout: (): Promise<{ success: boolean }> =>
    request<{ success: boolean }>('/auth/logout', { method: 'POST' }),
};

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 週次レポート API クライアント。一覧・詳細・作成・更新・公開・公開取消・削除・本文画像アップロードを提供する。閲覧は全メンバー、書き込みは編集者のみ（403 はサーバ側で判定）。
 * @graph-connects /weekly-reports [calls] 週次レポート一覧・作成
 * @graph-connects /weekly-reports/{id} [calls] 週次レポート詳細・更新・削除
 * @graph-connects /weekly-reports/images [calls] 本文埋め込み画像のアップロード
 */
export const weeklyReportApi = {
  list: (): Promise<WeeklyReportListResponse> =>
    request<WeeklyReportListResponse>('/weekly-reports'),
  get: (id: string): Promise<WeeklyReportDetailResponse> =>
    request<WeeklyReportDetailResponse>(`/weekly-reports/${encodeURIComponent(id)}`),
  create: (input: WeeklyReportInput): Promise<{ report: WeeklyReport }> =>
    request<{ report: WeeklyReport }>('/weekly-reports', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  update: (id: string, input: WeeklyReportInput): Promise<{ report: WeeklyReport }> =>
    request<{ report: WeeklyReport }>(`/weekly-reports/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  publish: (id: string): Promise<{ report: WeeklyReport }> =>
    request<{ report: WeeklyReport }>(`/weekly-reports/${encodeURIComponent(id)}/publish`, {
      method: 'POST',
    }),
  unpublish: (id: string): Promise<{ report: WeeklyReport }> =>
    request<{ report: WeeklyReport }>(`/weekly-reports/${encodeURIComponent(id)}/unpublish`, {
      method: 'POST',
    }),
  remove: (id: string): Promise<{ success: boolean }> =>
    request<{ success: boolean }>(`/weekly-reports/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  uploadImage: (input: { mimeType: string; data: string }): Promise<{ image: { id: string } }> =>
    request<{ image: { id: string } }>('/weekly-reports/images', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
};

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business アップロード済みの週次レポート画像の表示 URL を組み立てる。本文（tiptap JSON / HTML）の img src に永続化され、認証済みの閲覧者はブラウザの Cookie 認証で画像を取得できる。
 * @graph-connects /weekly-reports/images/{id} [calls] 本文埋め込み画像の配信
 */
export function weeklyReportImageUrl(imageId: string): string {
  return `${config.apiUrl}/weekly-reports/images/${encodeURIComponent(imageId)}`;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business acm_records API クライアント。日次の4ブロック25指標（/kpi/records）を期間指定で取得する。
 * @graph-connects /kpi/records [calls] acm_records 日次行
 */
export const kpiApi = {
  getRecords: (params?: { from?: string; to?: string }): Promise<RecordsResponse> => {
    const qs = new URLSearchParams();
    if (params?.from) {
      qs.set('from', params.from);
    }
    if (params?.to) {
      qs.set('to', params.to);
    }
    const query = qs.toString();
    return request<RecordsResponse>(query ? `/kpi/records?${query}` : '/kpi/records');
  },
};

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business revenue__daily API クライアント。売上/前受金の2ブロック12指標（/revenue/records）を期間指定で取得し、検算ログ履歴（/revenue/validation-logs）と rawデータ（/revenue/raw-data）を取得する。
 * @graph-connects /revenue/records [calls] revenue__daily 日次行
 * @graph-connects /revenue/validation-logs [calls] revenue__validation_log 履歴
 * @graph-connects /revenue/raw-data [calls] database_no_embedding rawデータ
 */
export const revenueApi = {
  getRecords: (params?: { from?: string; to?: string }): Promise<RevenueRecordsResponse> =>
    request<RevenueRecordsResponse>(dateRangeUrl('/revenue/records', params?.from, params?.to)),
  getValidationLogs: (): Promise<ValidationLogsResponse> =>
    request<ValidationLogsResponse>('/revenue/validation-logs'),
  getRawData: (table: RawDataTableKey): Promise<RawDataResponse> =>
    request<RawDataResponse>(`/revenue/raw-data?table=${encodeURIComponent(table)}`),
};

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 画面マップ API クライアント。画面ツリー取得（GET /code-map/screens）と 画面メタ保存（PUT /code-map/screens/meta）。route_path はボディで渡す（`/` を含む route_path を URL パスに埋め込むと Cloudflare/Edge の %2F 正規化でルートが壊れ保存失敗するため）。
 * @graph-connects /code-map/screens [calls] 画面ツリー取得
 */
export const codeMapApi = {
  getScreens: (): Promise<ScreensResponse> => request<ScreensResponse>('/code-map/screens'),
  saveMeta: (routePath: string, input: ScreenMetaInput): Promise<ScreenMetaResponse> =>
    // route_path はボディに含める。URL パスに `/` 入り route_path を埋め込むと、
    // encodeURIComponent しても Cloudflare/Edge プロキシが %2F を `/` に正規化し、
    // ルート不一致（404）で保存に失敗する。ボディなら正規化の影響を受けない。
    request<ScreenMetaResponse>('/code-map/screens/meta', {
      method: 'PUT',
      body: JSON.stringify({ routePath, ...input }),
    }),
};

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business リリース履歴 API クライアント。メンズ main マージPRの発信トーン要約タイムライン（/release-history/timeline）をカテゴリ・UX影響絞り込み付きで取得する。
 * @graph-connects /release-history/timeline [calls] リリース履歴タイムライン
 */
export const releaseHistoryApi = {
  getTimeline: (params?: { category?: string; uxImpact?: string }): Promise<TimelineResponse> => {
    const qs = new URLSearchParams();
    if (params?.category) {
      qs.set('category', params.category);
    }
    if (params?.uxImpact) {
      qs.set('uxImpact', params.uxImpact);
    }
    const query = qs.toString();
    return request<TimelineResponse>(
      query ? `/release-history/timeline?${query}` : '/release-history/timeline',
    );
  },
};

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business データカタログ API クライアント。BQ メタデータの3層構造概要（GET /data-catalog/overview）とテーブルのカラム詳細（GET /data-catalog/tables/:ds/:tbl/columns）を取得する。
 * @graph-connects /data-catalog/overview [calls] データカタログ概要
 * @graph-connects /data-catalog/tables/{datasetId}/{tableId}/columns [calls] カラム詳細
 */
/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 退会者分析 API クライアント。サブスクメトリクス・解約アンケート・月次トレンドを取得する。
 * @graph-connects /withdrawal-analysis [calls] Withdrawal Analysis dashboard endpoint
 */
export const withdrawalAnalysisApi = {
  getData: (): Promise<WithdrawalAnalysisResponse> =>
    request<WithdrawalAnalysisResponse>('/withdrawal-analysis'),
};

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 問い合わせ分析 API クライアント。ダッシュボード・チャット一覧・チャットの取り込み状況を取得する。
 * @graph-connects /cs-inquiry/dashboard [calls] ダッシュボードデータ
 * @graph-connects /cs-inquiry/chats [calls] チャット一覧
 * @graph-connects /cs-inquiry/import-status [calls] 状態を取り込めていないチャット数
 */
export const csInquiryApi = {
  getDashboard: (params?: { from?: string; to?: string }): Promise<CsDashboardResponse> => {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    const query = qs.toString();
    return request<CsDashboardResponse>(
      query ? `/cs-inquiry/dashboard?${query}` : '/cs-inquiry/dashboard',
    );
  },
  getChats: (params?: {
    from?: string;
    to?: string;
    state?: string;
    limit?: number;
    offset?: number;
  }): Promise<CsChatsResponse> => {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    if (params?.state) qs.set('state', params.state);
    if (params?.limit !== undefined) qs.set('limit', String(params.limit));
    if (params?.offset !== undefined) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return request<CsChatsResponse>(query ? `/cs-inquiry/chats?${query}` : '/cs-inquiry/chats');
  },
  getChatDetail: (userChatId: string): Promise<CsChatDetailResponse> =>
    request<CsChatDetailResponse>(`/cs-inquiry/chats/${encodeURIComponent(userChatId)}`),
  getImportStatus: (): Promise<CsImportStatusResponse> =>
    request<CsImportStatusResponse>('/cs-inquiry/import-status'),
  runAiAnalysis: (params: { from?: string; to?: string }): Promise<SavedCsAiAnalysisResponse> =>
    request('/cs-inquiry/ai-analysis', { method: 'POST', body: JSON.stringify(params) }),
  getAiAnalysisLatest: (): Promise<SavedCsAiAnalysisResponse | null> =>
    request('/cs-inquiry/ai-analysis/latest'),
  getAiAnalysisHistory: (): Promise<{ items: CsAiAnalysisHistoryItem[] }> =>
    request('/cs-inquiry/ai-analysis/history'),
  getAiAnalysisDetail: (id: string): Promise<SavedCsAiAnalysisResponse | null> =>
    request(`/cs-inquiry/ai-analysis/${encodeURIComponent(id)}`),
};

/** @graph-connects none */
export const dataCatalogApi = {
  getOverview: (): Promise<CatalogOverviewResponse> =>
    request<CatalogOverviewResponse>('/data-catalog/overview'),
  getColumns: (datasetId: string, tableId: string): Promise<CatalogColumnsResponse> =>
    request<CatalogColumnsResponse>(
      `/data-catalog/tables/${encodeURIComponent(datasetId)}/${encodeURIComponent(tableId)}/columns`,
    ),
};

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business PORTER 通知管理 API クライアント。チャンネル CRUD・ルール CRUD・テスト通知を提供する。
 * @graph-connects /notifications/channels [calls] チャンネル一覧・作成・更新・削除
 * @graph-connects /notifications/rules [calls] ルール一覧・作成・更新・削除
 * @graph-connects /notifications/test [calls] テスト通知送信
 */
export const notificationApi = {
  listChannels: (): Promise<NotifChannelListResponse> =>
    request<NotifChannelListResponse>('/notifications/channels'),
  createChannel: (input: SlackChannelInput): Promise<NotifChannelMutationResponse> =>
    request<NotifChannelMutationResponse>('/notifications/channels', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateChannel: (
    channelId: string,
    input: SlackChannelInput,
  ): Promise<NotifChannelMutationResponse> =>
    request<NotifChannelMutationResponse>(
      `/notifications/channels/${encodeURIComponent(channelId)}`,
      { method: 'PUT', body: JSON.stringify(input) },
    ),
  removeChannel: (channelId: string): Promise<{ deleted: boolean }> =>
    request<{ deleted: boolean }>(`/notifications/channels/${encodeURIComponent(channelId)}`, {
      method: 'DELETE',
    }),
  listRules: (): Promise<RuleListResponse> => request<RuleListResponse>('/notifications/rules'),
  createRule: (input: NotificationRuleInput): Promise<RuleMutationResponse> =>
    request<RuleMutationResponse>('/notifications/rules', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateRule: (
    notificationType: string,
    input: NotificationRuleInput,
  ): Promise<RuleMutationResponse> =>
    request<RuleMutationResponse>(`/notifications/rules/${encodeURIComponent(notificationType)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  removeRule: (notificationType: string): Promise<{ deleted: boolean }> =>
    request<{ deleted: boolean }>(`/notifications/rules/${encodeURIComponent(notificationType)}`, {
      method: 'DELETE',
    }),
  sendTest: (input: TestNotificationInput): Promise<TestNotificationResponse> =>
    request<TestNotificationResponse>('/notifications/test', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
};

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business スタイリストパフォーマンス分析 API クライアント。KPIサマリ・評価推移・履歴一覧・詳細を取得する。
 * @graph-connects /stylist-performance/summary [calls] KPIサマリ集計
 * @graph-connects /stylist-performance/evaluation-trend [calls] GOOD/BAD評価推移
 * @graph-connects /stylist-performance/orders [calls] レンタル履歴一覧
 * @graph-connects /stylist-performance/orders/:id [calls] オーダー詳細
 */
export const stylistPerformanceApi = {
  getSummary: (month?: string, stylistId?: string): Promise<KpiSummaryResponse> => {
    const qs = new URLSearchParams();
    if (month) qs.set('month', month);
    if (stylistId) qs.set('stylistId', stylistId);
    const query = qs.toString();
    return request<KpiSummaryResponse>(
      query ? `/stylist-performance/summary?${query}` : '/stylist-performance/summary',
    );
  },
  getEvaluationTrend: (months?: number, stylistId?: string): Promise<EvaluationTrendResponse> => {
    const qs = new URLSearchParams();
    if (months !== undefined) qs.set('months', String(months));
    if (stylistId) qs.set('stylistId', stylistId);
    const query = qs.toString();
    return request<EvaluationTrendResponse>(
      query
        ? `/stylist-performance/evaluation-trend?${query}`
        : '/stylist-performance/evaluation-trend',
    );
  },
  getOrders: (
    params: Record<string, string | number | undefined>,
  ): Promise<RentalHistoryResponse> => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) qs.set(k, String(v));
    }
    const query = qs.toString();
    return request<RentalHistoryResponse>(
      query ? `/stylist-performance/orders?${query}` : '/stylist-performance/orders',
    );
  },
  getOrderDetail: (orderId: number): Promise<OrderDetailResponse> =>
    request<OrderDetailResponse>(`/stylist-performance/orders/${String(orderId)}`),
};

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 会員管理 API クライアント。会員一覧検索・プロフィール・決済・オーダー・チケット・クーポン・住所を取得する。
 * @graph-connects /members [calls] 会員一覧検索
 * @graph-connects /members/{id}/profile [calls] 会員プロフィール
 * @graph-connects /members/{id}/payments [calls] 決済履歴
 * @graph-connects /members/{id}/orders [calls] オーダー履歴
 * @graph-connects /members/{id}/tickets [calls] チケット履歴
 * @graph-connects /members/{id}/coupons [calls] クーポン履歴
 * @graph-connects /members/{id}/addresses [calls] 住所一覧
 */
export const memberApi = {
  getList: (params?: {
    page?: number;
    limit?: number;
    arthurUserId?: number;
    lastName?: string;
    firstName?: string;
    email?: string;
    tel?: string;
    subscriptionStatus?: string;
    rentalStatuses?: readonly string[];
    planName?: string;
    ticketHolding?: string;
    environment?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<MemberListResponse> => {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.limit !== undefined) qs.set('limit', String(params.limit));
    if (params?.arthurUserId !== undefined) qs.set('arthurUserId', String(params.arthurUserId));
    if (params?.lastName) qs.set('lastName', params.lastName);
    if (params?.firstName) qs.set('firstName', params.firstName);
    if (params?.email) qs.set('email', params.email);
    if (params?.tel) qs.set('tel', params.tel);
    if (params?.subscriptionStatus) qs.set('subscriptionStatus', params.subscriptionStatus);
    // 何も選ばれていなければパラメータ自体を送らない（空文字を送ると API 側で弾かれる）
    if (params?.rentalStatuses !== undefined && params.rentalStatuses.length > 0) {
      qs.set('rentalStatuses', params.rentalStatuses.join(','));
    }
    if (params?.planName) qs.set('planName', params.planName);
    if (params?.ticketHolding) qs.set('ticketHolding', params.ticketHolding);
    if (params?.sortBy) qs.set('sortBy', params.sortBy);
    if (params?.sortOrder) qs.set('sortOrder', params.sortOrder);
    const query = qs.toString();
    // STG は本番とは別の権限で守られた別パス。同じパスの下位に置くと本番権限まで要求される
    const path = params?.environment === 'staging' ? '/staging-members' : '/members';
    return request<MemberListResponse>(query ? `${path}?${query}` : path);
  },
  getProfile: (id: number): Promise<MemberProfileResponse> =>
    request<MemberProfileResponse>(`/members/${String(id)}/profile`),
  getPayments: (id: number): Promise<PaymentHistoryResponse> =>
    request<PaymentHistoryResponse>(`/members/${String(id)}/payments`),
  getOrders: (id: number): Promise<OrderHistoryResponse> =>
    request<OrderHistoryResponse>(`/members/${String(id)}/orders`),
  getTickets: (id: number): Promise<TicketHistoryResponse> =>
    request<TicketHistoryResponse>(`/members/${String(id)}/tickets`),
  getCoupons: (id: number): Promise<CouponHistoryResponse> =>
    request<CouponHistoryResponse>(`/members/${String(id)}/coupons`),
  getAddresses: (id: number): Promise<AddressListResponse> =>
    request<AddressListResponse>(`/members/${String(id)}/addresses`),
};

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business ファネル分析 API クライアント。メンズ登録ファネル（会員登録→サブスク→スタイリング情報登録）の日次集計を期間指定で取得する。
 * @graph-connects /funnel-analysis/registration [calls] 登録ファネル日次集計
 */
export const funnelAnalysisApi = {
  getRegistrationFunnel: (params?: { from?: string; to?: string }): Promise<FunnelResponse> => {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    const query = qs.toString();
    return request<FunnelResponse>(
      query ? `/funnel-analysis/registration?${query}` : '/funnel-analysis/registration',
    );
  },
};

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 突破率 API クライアント。コホート別の1〜3ヶ月目突破率データを期間指定で取得する。
 * @graph-connects /breakthrough/records [calls] 1〜3ヶ月目突破率コホート行
 */
export const breakthroughApi = {
  getRecords: (params?: { from?: string; to?: string }): Promise<BreakthroughResponse> =>
    request<BreakthroughResponse>(dateRangeUrl('/breakthrough/records', params?.from, params?.to)),
};

export { budgetActualApi } from './budget-actual-api.js';
export { couponUsageApi } from './coupon-usage-api.js';
export { memberDemographicsApi } from './member-demographics-api.js';
export { purchaseAnalysisApi } from './purchase-analysis-api.js';
export { keyIssueApi } from './key-issue-api.js';
export { reportApi } from './report-api.js';
